import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  ThumbsUp,
  CheckCircle,
  AlertTriangle,
  Loader2,
  MessageSquare,
  BarChart3
} from 'lucide-react';
import { Typewriter } from '../components/Typewriter';

interface Feedback {
  session_id: string;
  overall_assessment: string;
  strengths: string[];
  areas_for_improvement: string[];
  missed_topics: string[];
  communication_quality: string;
  cultural_competence: string;
  recommendations: string[];
}

const FeedbackReceivePage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFeedback = async () => {
    try {
      const response = await apiClient.post(`/api/feedback/generate`, {
        session_id: sessionId
      });
      setFeedback(response.data);
    } catch (error: any) {
      console.error('Failed to fetch feedback:', error);
      setError(error.response?.data?.detail || 'Failed to generate feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleBackToPatients = () => {
    navigate('/patients');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
        <p className="text-muted-foreground">Generating AI feedback...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <h2 className="text-xl font-bold text-red-700 mb-2">Error Generating Feedback</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={handleBackToPatients}>Back to Patients</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToPatients}
            className="text-primary-foreground hover:bg-primary-foreground/10"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-bold">AI-Generated Feedback</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 space-y-6 max-w-4xl">
        {feedback && (
          <>
            {/* Overall Assessment */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Overall Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed">
                  <Typewriter text={feedback.overall_assessment} speed={5} />
                </p>
              </CardContent>
            </Card>

            {/* Strengths */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ThumbsUp className="h-5 w-5 text-green-600" />
                  <CardTitle className="text-xl">Strengths</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {feedback.strengths.map((strength, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{strength}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Areas for Improvement */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <CardTitle className="text-xl">Areas for Improvement</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {feedback.areas_for_improvement.map((area, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{area}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Missed Topics */}
            {feedback.missed_topics.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl">Missed Topics</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Important topics that could have been explored:
                  </p>
                  <ul className="space-y-2">
                    {feedback.missed_topics.map((topic, index) => (
                      <li key={index} className="text-sm pl-4 border-l-2 border-gray-300">
                        {topic}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Communication Quality */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Communication Quality</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed">{feedback.communication_quality}</p>
              </CardContent>
            </Card>

            {/* Cultural Competence */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Cultural Competence</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed">{feedback.cultural_competence}</p>
              </CardContent>
            </Card>

            {/* Recommendations */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-xl">Recommendations for Next Time</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {feedback.recommendations.map((recommendation, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{recommendation}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="outline" size="lg" onClick={handleBackToPatients}>
                Back to Patients
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate(`/session/${sessionId}`)}>
                <MessageSquare className="h-4 w-4 mr-2" />
                Review Conversation
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate(`/evaluation/${sessionId}`)}>
                <BarChart3 className="h-4 w-4 mr-2" />
                View Evaluation
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackReceivePage;
